import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onSelect,
  placeholder = "Enter your property address...",
  className = "",
  inputClassName = "",
  testId = "address-autocomplete"
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onChangeRef = useRef(onChange);
  const [isLoaded, setIsLoaded] = useState(false);

  // Keep refs updated
  useEffect(() => {
    onSelectRef.current = onSelect;
    onChangeRef.current = onChange;
  }, [onSelect, onChange]);

  useEffect(() => {
    // Check if Google Maps is loaded
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsLoaded(true);
        return true;
      }
      return false;
    };

    if (checkGoogleMaps()) return;

    // Poll for Google Maps to load
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;
    
    // Don't reinitialize if already done
    if (autocompleteRef.current) return;

    // Initialize Google Places Autocomplete
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        componentRestrictions: { country: "us" },
        fields: ["address_components", "formatted_address", "geometry"],
        types: ["address"],
      }
    );

    // Handle place selection
    autocompleteRef.current.addListener("place_changed", () => {
      const place = autocompleteRef.current.getPlace();
      
      if (place && place.formatted_address) {
        // Update the input value using ref
        if (onChangeRef.current) {
          onChangeRef.current(place.formatted_address);
        }
        
        // Call onSelect callback if provided - use ref to get latest callback
        if (onSelectRef.current) {
          onSelectRef.current(place);
        }
      }
    });

    // Cleanup
    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [isLoaded]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  // Prevent form submission when pressing Enter on autocomplete dropdown
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Check if autocomplete dropdown is open
      const pacContainer = document.querySelector(".pac-container");
      if (pacContainer && pacContainer.style.display !== "none") {
        e.preventDefault();
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10 pointer-events-none" />
      <Input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={`pl-12 ${inputClassName}`}
        data-testid={testId}
        autoComplete="off"
      />
    </div>
  );
};

export default AddressAutocomplete;
